import { useNavigate, useParams } from 'react-router-dom';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import React, { useEffect, useRef, useState } from 'react';
import { getPageContent, toPageContentDTO } from '../../../services/page.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { InterlocutorsReferenceDto } from '../../../services/dtos/interlocutors-reference.dto';
import { getCivilities, getInterlocutorsTypes } from '../../../services/references.service';
import MovingInStepThreeUi from '../../../components/user-interfaces/moving-in/moving-in-step-three/moving-in-step-three.ui';
import { PageContentDto } from '../../../services/dtos/page-content.dto';
import { movingInService } from '../../../services/moving-in.service';
import { AxiosError } from 'axios';
import { MovingInFinishDto } from '../../../services/dtos/moving-in-finish.dto';
import { MovingInDto } from '../../../services/dtos/moving-in-dto';
import useRecaptcha from '../../../hooks/google-recaptcha.hook';
import { AuthContextType, useAuth } from '../../../hooks/use-auth.hook';
import { getUser } from '../../../services/account.service';
import { UserDto } from '../../../services/dtos/user.dto';

/**
 * Page déménagement
 * @constructor
 */
export default function MovingInStepThreePage() {
    //navigate
    const navigate = useNavigate();
    //Params
    const { id } = useParams();
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //UseState
    const [countries, setCountries] = useState<ReferenceDto[]>([{ value: 'France', label: 'France' }]);
    const [validForm, setValidForm] = useState(true);
    const [country, setSelectedCountry] = useState<ReferenceDto>({ value: 'France', label: 'France' });
    const [incomingPersonCountry, setIncomingPersonCountry] = useState<ReferenceDto>({
        value: 'France',
        label: 'France',
    });
    const [incomingPerson, setIncomingPerson] = useState('Non');
    const [isMoral, setIsMoral] = React.useState(false);
    const [civilities, setCivilities] = useState<ReferenceDto[]>([]);
    const [interlocutors, setInterlocutors] = useState<InterlocutorsReferenceDto[]>([]);
    // Déclaration du loader pour l'affichage du Skeleton
    const [loading, setLoading] = useState<boolean>(true);
    const [user, setUser] = useState<UserDto>();
    //Création de données par défaut pour la page
    const [pageContent, setPageContent] = useState<PageContentDto>({
        content: undefined,
        title: undefined,
    });

    let auth: AuthContextType = useAuth(); // récupère l'utilisateur connecté

    const infosMovingIn = window.localStorage.getItem('movingInFinish');
    const [infosMovingInDto, setInfosMovingInDto] = useState<MovingInFinishDto>();

    //Use effect
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        if (infosMovingIn && infosMovingIn !== 'null') {
            setInfosMovingInDto(JSON.parse(infosMovingIn));
        } else {
            navigate('/');
        }

        if (auth.user?.userId) {
            getUser(auth.user?.userId)
                .then(result => {
                    setUser(result);
                })
                .catch(error => {
                    //Affichage du message d'erreur si la requete ne récupère rien
                    console.error("Erreur lors de la récupération des informations de l'utilisateur");
                    showAxiosMessageError(error, showMessage);
                });
        }

        //Récupération des textes de la page
        getPageContent('emmenage-etape-4')
            .then(async result => {
                //On met le résultat de la request dans la variable envoyée à la page
                setPageContent(await toPageContentDTO(result));

                setLoading(false);
            })
            .catch(error => {
                //Affichage du message d'erreur si la request ne récupère rien
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        //Récupération des pays à afficher dans le select
        getPageContent('adresse')
            .then(async result => {
                let countriesArray: ReferenceDto[] = [];
                JSON.parse(result[0].valeur, (key, value: string) => {
                    if (key !== null && key !== undefined && key !== '') {
                        countriesArray.push({ value: value, label: value });
                    }
                });
                setCountries(countriesArray);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        //Appel types d'interlocuteurs
        getInterlocutorsTypes()
            .then(result => {
                const data: InterlocutorsReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            id: e.id,
                            label: e.nom,
                            moralPerson: e.personneMorale,
                        };
                    });
                setInterlocutors(data);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            });

        //Appel des civilités
        getCivilities()
            .then(result => {
                const data: ReferenceDto[] =
                    result &&
                    result.map(e => {
                        return {
                            value: e.id,
                            label: e.nom,
                        };
                    });
                setCivilities(data);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            });
        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Recaptcha
    const { getRecaptchaToken } = useRecaptcha('jemmenage');

    //Handler utilisé lors du submit du formulaire
    const handleSumbitForm = (values: any) => {
        setValidForm(false);
        getRecaptchaToken()
            .then(token => {
                // On remet tout à jour pour l'envoi final
                const data: MovingInDto = {
                    //maj captcha
                    captcha: token,
                    //maj date emmenagement
                    dateEmmenagement: infosMovingInDto?.movingInDate,
                    //maj relevé
                    releveCompteur: infosMovingInDto?.meterReading,
                    //maj point d'installation
                    pointDinstallation: infosMovingInDto?.address?.pdiId,
                    //maj proprietaire
                    proprietaire: infosMovingInDto?.isOwner,
                    // maj numero de rue
                    numeroRue: values.numeroRue,
                    //maj type
                    type: '/interlocuteur_types/' + values.type,
                    // maj telephone portable
                    telPort: values.telPort ? '0' + values.telPort : '',
                    // maj telephone portable
                    telFixe: values.telFixe ? '0' + values.telFixe : '',
                    // maj mail
                    mail: values.mail,
                    // maj rue
                    rue: values.rue,
                    // maj complement (standardisé avec tous les champs)
                    complement: {
                        numeroRue: values.complement?.numeroRue || '',
                        rue: values.complement?.rue || '',
                        batiment: values.complement?.batiment || '',
                        etage: values.complement?.etage || '',
                        appartement: values.complement?.appartement || '',
                        escalier: values.complement?.escalier || '',
                        ville: values.complement?.ville || '',
                    },
                    // maj code postal
                    codePostal: parseInt(values.codePostal, 10),
                    // maj ville
                    ville: values.ville,
                    // maj boite postale
                    boitePostale: values.boitePostale || '',
                    // maj pays
                    pays: country.value,
                    // maj infos sortant
                    sortantInfos: incomingPerson === 'Oui',
                };
                if (incomingPerson === 'Oui') {
                    data.sortantComplement = {
                        numeroRue: values.sortantInfos.complement?.numeroRue || '',
                        rue: values.sortantInfos.complement?.rue || '',
                        batiment: values.sortantInfos.complement?.batiment || '',
                        etage: values.sortantInfos.complement?.etage || '',
                        appartement: values.sortantInfos.complement?.appartement || '',
                        escalier: values.sortantInfos.complement?.escalier || '',
                        ville: values.sortantInfos.complement?.ville || '',
                    };
                    data.sortantNumeroRue = values.sortantInfos.numeroRue;
                    data.sortantRue = values.sortantInfos.rue;
                    data.sortantCodePostal = parseInt(values.sortantInfos.codePostal, 10);
                    data.sortantVille = values.sortantInfos.ville;
                    data.sortantBoitePostale = values.sortantInfos.boitePostale || '';
                    data.sortantPays = incomingPersonCountry.value;
                }

                if (isMoral) {
                    data.siret = values.siret;
                    data.salaries = values.salaries;
                    data.raisonSocial = values.raisonSociale;
                } else {
                    data.civilite = '/civilites/' + values.civilite;
                    data.nom = values.nomUsage;
                    data.prenom = values.prenom;
                    data.dateNaissance = values.dateNaissance;
                }
                movingInService(data)
                    .then(result => {
                        if (result) {
                            window.localStorage.setItem('movingInFinish', JSON.stringify(null));
                            showMessage.successAlert("Votre demande d'emménagement a bien été prise en compte.");
                            navigate('/');
                        }
                    })
                    .catch((error: Error | AxiosError) => {
                        showAxiosMessageError(error, showMessage);
                    })
                    .finally(() => {
                        setValidForm(true);
                    });
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });
    };

    //handler lors du changement de pays
    const handleChangeCountry = (e: React.SyntheticEvent, v: ReferenceDto | null) => {
        if (v) {
            setSelectedCountry(v);
        }
    };

    //handler lors du changement de pays entrant
    const handleChangeCountryIncomingPerson = (e: React.SyntheticEvent, v: ReferenceDto | null) => {
        if (v) {
            setIncomingPersonCountry(v);
        }
    };

    /**
     * Méthode qui permet de gérer le radio bouton
     * @param event
     */
    const handleChangeIncomingPerson = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncomingPerson((event.target as HTMLInputElement).value);
    };

    /**
     * Changement de la variable isMoral au choix de l'interlocuteur (a une influence sur les champs affichés dans le formulaire
     * @param e déclencheur de l'évènement
     */
    const handleChangeInterlocutor = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        const selectedInterlocutor = interlocutors.find(interlocutor => interlocutor.id === e?.target?.value);
        setIsMoral(selectedInterlocutor?.moralPerson === undefined ? false : selectedInterlocutor.moralPerson);
    };

    //On reconstruit la liste car elle n'est pas confirme pour le composant
    const interlocutorsSelection = interlocutors.map(interlocutor => {
        return {
            value: interlocutor.id,
            label: interlocutor.label,
        };
    });

    //Retourne la formulaire
    return (
        <MovingInStepThreeUi
            user={user}
            pageContent={pageContent}
            loading={loading}
            handleSumbitForm={handleSumbitForm}
            handleChangeCountry={handleChangeCountry}
            handleChangeCountryIncomingPerson={handleChangeCountryIncomingPerson}
            countriesSelection={countries}
            handleChangeIncomingPerson={handleChangeIncomingPerson}
            incomingPerson={incomingPerson}
            isMoral={isMoral}
            handleChangeInterlocutor={handleChangeInterlocutor}
            interlocutorsSelection={interlocutorsSelection}
            civilities={civilities}
            validate={validForm}
            contractId={id}
            selectedCountry={country}
            infosMovingInDto={infosMovingInDto}
        />
    );
}
